<template>
  <aside
    class="VisionSidebar"
    :class="[
      {
        'VisionSidebar--visible': visible,
        'VisionSidebar--overflow-hidden': preventScroll,
        'VisionSidebar--fixed-title': fixedTitle,
        'VisionSidebar--no-title': !title && !$slots.customTitle,
        'VisionSidebar--plain-title': title || (!title && !$slots.customTitle),
        'VisionSidebar--custom-title': $slots.customTitle
      },
    ]"
  >
    <div class="VisionSidebar__background" />
    <div
      class="VisionSidebar__overlay"
      @click="close"
    />

    <div
      class="VisionSidebar__outer"
      :style="{ maxWidth: (maxWidth ? `${maxWidth}px` : 'max-content') }"
    >
      <div
        class="VisionSidebar__mobile-tab"
        @touchstart="handleTouch"
      />
      <div
        class="VisionSidebar__content"
      >
        <div
          class="VisionSidebar__title-row"
        >
          <template
            v-if="$slots.customTitle"
          >
            <slot name="customTitle" />
          </template>
          <VisionHeading
            v-else-if="title"
            class="VisionSidebar__title"
            :tag="headingTag || 'h5'"
            size="h3"
          >
            {{ title }}
          </VisionHeading>
          <button
            class="VisionSideBar__close-button"
            @click="close"
          >
            <VisionIcon
              size="stretch"
              icon="cross"
              colour="black"
            />
          </button>
        </div>
        <slot />
      </div>
    </div>
  </aside>
</template>

<script>
import VisionIcon from '../VisionIcon/VisionIcon.vue'
import VisionHeading from '../VisionHeading/VisionHeading.vue'

export default {
  name: 'VisionSidebar',

  components: {
    VisionIcon,
    VisionHeading,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: Number,
      default: 400,
    },
    preventScroll: {
      type: Boolean,
      default: false,
    },
    headingTag: {
      type: String,
      default: 'h5',
      validator(value) {
        return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(value)
      },
    },
    fixedTitle: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: ''
    }
  },

  emits: ['block-body-scroll', 'close'],

  watch: {
    visible() {
      if (this.visible) this.$emit('block-body-scroll', true)
      else this.$emit('block-body-scroll', false)
    },
  },

  methods: {
    close() {
      this.$emit('close', false)
    },
    handleTouch(event) {
    if (event.touches.length === 1) {
      const touch = event.touches[0];
      const target = event.target;

      // Only allow on the tab
      if (target.classList.contains('VisionSidebar__mobile-tab')) {
        const initialTouchY = touch.clientY;
        const moveHandler = (event) => {
          if (event.touches.length === 1) {
            const currentTouchY = event.touches[0].clientY;
            const deltaY = currentTouchY - initialTouchY;

            if (deltaY > 50) {
              this.close();
            }
          }
        };

        const endHandler = () => {
          document.removeEventListener('touchmove', moveHandler);
          document.removeEventListener('touchend', endHandler);
        };

        document.addEventListener('touchmove', moveHandler);
        document.addEventListener('touchend', endHandler);
      }
    }
  },
}
}
</script>

<style lang="postcss">
.VisionSidebar {
  --sidebar-margin-top: 5vh;
  --mobile-toggle-height: 32px;

  @screen md {
    --sidebar-margin-top: 0px;
    --mobile-toggle-height: 0px;
  }
  
  @apply fixed z-[100] top-0 left-0
    w-full h-full
    flex
    invisible
    transition-all duration-100 delay-0 md:duration-0 md:delay-500;
}

.VisionSidebar__overlay {
  @apply
    flex-grow
    overflow-visible
    cursor-pointer;

    &::after {
      @apply content-[''] absolute top-0 left-0 w-full h-full;
    }
}

.VisionSidebar__background {
  @apply absolute top-0 left-0
    w-screen h-screen
    bg-black
    bg-opacity-0
    backdrop-blur-0
    transition-all
    duration-500;
}

.VisionSidebar__outer {
  @apply relative
    w-full h-full
    mt-[var(--sidebar-margin-top)]
    flex flex-col
    bg-white
    transition-all
    duration-500
    md:translate-x-full;
  
  @screen mdMax {
    @apply rounded-[24px] translate-x-0
    overflow-hidden;
    max-width: none !important;
    transform: translateY(100%) translateZ(0);
  }
}

.VisionSidebar__mobile-tab {
  @apply h-[var(--mobile-toggle-height)] pt-2 flex justify-center relative overflow-visible z-[2] md:hidden;

  &::before {
    @apply content-[''] h-1 w-10 bg-grey-400;
  }

  &::after {
    @apply content-[''] h-1.5 w-full absolute top-[calc(100%_-_1px)] left-0;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)
  }
}

.VisionSidebar__content {
  @apply relative
    w-full h-full
    overflow-y-auto
    text-off-black;
}

.VisionSidebar__title-row {
  @apply
    flex items-start justify-between
    bg-white
    z-[1];
}

.VisionSidebar__title {
  @apply w-full py-5;
}

.VisionSideBar__close-button {
  @apply h-full flex items-center justify-center w-8 p-2.5 pr-0;
}

/*
  Modifiers

  --overflow-hidden
  --visible
  --plain-title
  --no-title
*/

.VisionSidebar--overflow-hidden {
  .VisionSidebar__content {
    @apply overflow-hidden;
  }
}

.VisionSidebar--no-title {
  .VisionSidebar__title-row {
    @apply justify-end;
  }
}

.VisionSidebar--visible {
  @apply visible
      transition-all duration-0 delay-0;

  .VisionSidebar__outer {
    @screen md {
      transform: translateX(0%) translateZ(0);
    }
  
    @screen mdMax {
      transform: translateY(0%) translateZ(0);
    }
  }

  .VisionSidebar__background {
    @apply bg-opacity-80 delay-0
    backdrop-blur-sm;
  }
}

.VisionSidebar--plain-title {
  .VisionSidebar__title-row {
    @apply px-4 py-4 items-center;
  }
}

.VisionSidebar--fixed-title {
  .VisionSidebar__title-row {
    @apply sticky top-[-2px];
  }
}

.VisionSidebar--custom-title {
  .VisionSideBar__close-button {
    @apply w-14 pt-[12px] pr-[24px] md:pt-[24px];
  }
}
</style>
